import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import TextArea from '@/common/components/TextArea'
import { ErrorResponse } from '@/common/types/errorResponse'
import { handleServerError } from '@/common/utils/handleServerError'
import { useReviewPriceSurvey } from '@/features/privateCompanies/hooks/priceSurvey/useReviewPriceSurvey'
import {
  rejectPriceSurveySchema,
  RejectPriceSurveyValidationSchema,
} from '@/features/privateCompanies/schemas/priceSurvey/reject.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface RejectPriceSurveyModalProps {
  disabled?: boolean
}

export const RejectPriceSurveyModal = ({ disabled }: RejectPriceSurveyModalProps) => {
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [baseError, setBaseError] = useState('')

  const {
    register,
    handleSubmit,
    reset: resetForm,
    setError,
    formState: { errors },
  } = useForm<RejectPriceSurveyValidationSchema>({
    resolver: zodResolver(rejectPriceSurveySchema),
    defaultValues: {
      rejection_reason: '',
    },
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['companyPriceSurveys'] })
    queryClient.invalidateQueries({ queryKey: ['companyPriceSurvey', id] })
    resetForm()
    setIsOpen(false)
  }

  const onError = (error: ErrorResponse) => {
    handleServerError({ error, setBaseError, setError })
  }

  const { mutate: rejectPriceSurvey, isPending: rejectPriceSurveyIsPending } = useReviewPriceSurvey({
    priceSurveyId: id,
    review: 'reject',
    onSuccess,
    onError,
  })

  const onSubmit: SubmitHandler<RejectPriceSurveyValidationSchema> = (data) => {
    setBaseError('')
    rejectPriceSurvey(data)
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          type="button"
          variant="yellow"
          className="w-[10.25rem]"
          disabled={disabled}
          operationKlassToCheckPermission="Admin::Client::PriceSurveys::Reject"
        >
          Declinar
        </Button>
      }
      triggerDisabled={disabled}
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={rejectPriceSurveyIsPending}
      title="Declinar pesquisa de preços"
      titleClass="text-2xl font-bold mb-2"
      subtitle="Preencha o motivo para declinar esta pesquisa."
      body={
        <form className="flex flex-col gap-4">
          {baseError && <p className="text-sm text-danger-soft">{baseError}</p>}
          <TextArea
            label="Justificativa"
            className="text-white-800"
            placeholder="Escreva aqui sua justificativa"
            errorMessage={errors?.rejection_reason?.message}
            {...register('rejection_reason')}
            markAsRequired
          />
        </form>
      }
    />
  )
}
